import { Injectable, EventEmitter, Injector } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Observable, Subscription, firstValueFrom, timer } from 'rxjs';
import { UserProfile, Organization } from '../../shared/models/user-profile';
import { AppSettingsService, FirestoreService } from '@core';
import { ShopifyFirestoreService } from '@core/services/shopify-firestore.service';
import { LoggingService } from '@core/services/logging.service';
import { DexieDbProvider, RolesEnum } from '@shared';
import { SignalrService } from '@core/services/signalr.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { EdaraSetupService } from '@core/services/edara-setup.service';
import { KeycloakAuthService } from '@core/services/keycloak-auth.service';
import { PreferredOrganizationService } from '@core/services/preferred-organization.service';
import { EmbedService } from '@core/services/embed.service';
import { PortalBridgeService } from '@core/services/portal-bridge.service';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  public userLoggedIn$: EventEmitter<UserProfile> = new EventEmitter();
  public userLoggedOut$: EventEmitter<string> = new EventEmitter();
  public tenantSwitched$: EventEmitter<string> = new EventEmitter();

  private _accessToken: string = '';
  public get accessToken(): string {
    if (!this._accessToken) {
      this._accessToken = localStorage.getItem('accessToken') ?? '';
    }

    return this._accessToken;
  }

  private _userProfile?: UserProfile;
  public get userProfile(): UserProfile | undefined {
    if (!this._userProfile) {
      let userProfileJson = localStorage.getItem('userProfile');
      this._userProfile = userProfileJson ? JSON.parse(userProfileJson) : undefined;
    }

    return this._userProfile;
  }

  private _userRoles?: string[];
  public get userRoles(): string[] {
    if (!this._userRoles) {
      this._userRoles = this.getRolesFromToken();
    }

    return this._userRoles;
  }

  private _userPermissions?: string[];
  public get userPermissions(): string[] {
    if (!this._userPermissions) {
      this._userPermissions = this.getPermissionsFromToken();
    }

    return this._userPermissions;
  }

  private _currentTenant: string = '';
  public get currentTenant(): string {
    if (!this._currentTenant) {
      this._currentTenant = localStorage.getItem('currentTenant') ?? '';
    }

    return this._currentTenant;
  }
  public set currentTenant(tenantId: string) {
    if (tenantId) {
      this._currentTenant = tenantId;
      // Set the current tenant into localStorage
      localStorage.setItem('currentTenant', tenantId);
    }
  }

  private _userOrganizations?: Organization[];

  public get currentUserId(): string {
  const id = this.userProfile?.id ?? '';
  const AUTH0_PREFIX = 'auth0|';
  return id.startsWith(AUTH0_PREFIX) ? id.slice(AUTH0_PREFIX.length) : id;
}

  public get currentUserEmail(): string {
    return this.userProfile ? this.userProfile.email : '';
  }

  public get userOrganizations(): Organization[] {
    if (!this._userOrganizations) {
      const stored = localStorage.getItem('userOrganizations');
      this._userOrganizations = stored ? JSON.parse(stored) : [];
    }
    return this._userOrganizations ?? [];
  }

  private _currentOrgFor?: string;
  private _currentOrgList?: Organization[];
  private _currentOrgCached?: Organization;
  public get currentOrganization(): Organization | undefined {
    // Memoize by (token, org-list) so repeated template reads return a STABLE
    // object reference and don't re-decode the JWT. Without this, this getter
    // rebuilt a new object (and decoded the token 2-3x) on every change-detection
    // pass — read per-org in the toolbar switcher it cost seconds of blocking.
    const token = this.accessToken;
    const orgs = this.userOrganizations;
    if (this._currentOrgFor === token && this._currentOrgList === orgs) {
      return this._currentOrgCached;
    }

    let result: Organization | undefined;
    const activeOrg = this.getActiveOrganizationFromToken();
    if (activeOrg) {
      // Try to match against the loaded org list (for keycloakOrgId); fall back to a synthetic org.
      const matched = orgs.find(o => o.name === activeOrg.name);
      const base: Organization = matched
        ? { ...matched }
        : { keycloakOrgId: '', name: activeOrg.name };
      base.tenantId = this.getTenantIdFromToken();
      base.edaraUserId = this.getEdaraUserIdFromToken();
      result = base;
    }

    this._currentOrgFor = token;
    this._currentOrgList = orgs;
    this._currentOrgCached = result;
    return result;
  }

  private _edaraCoreApiBaseUrl: string = '';
  private _shopifyCoreApiBaseUrl: string = '';
  private _loggingService: LoggingService | null = null;

  // Lazy getter to avoid circular dependency (LoggingService depends on AuthService)
  private get loggingService(): LoggingService {
    if (!this._loggingService) {
      this._loggingService = this.injector.get(LoggingService);
    }
    return this._loggingService;
  }

  constructor(
    private injector: Injector,
    private httpClient: HttpClient,
    private router: Router,
    private firestoreService: FirestoreService,
    private shopifyFirestoreService: ShopifyFirestoreService,
    private localDbProvider: DexieDbProvider,
    private signalRService: SignalrService,
    private edaraSetupService: EdaraSetupService,
    private translateService: TranslateService,
    private snackBar: MatSnackBar,
    private keycloakAuthService: KeycloakAuthService,
    private preferredOrganizationService: PreferredOrganizationService,
    private embedService: EmbedService,
    private portalBridge: PortalBridgeService
  ) {
    this._edaraCoreApiBaseUrl = AppSettingsService.appSettings.edaraCoreApi.baseUrl;
    if (this._edaraCoreApiBaseUrl.endsWith('/')) {
      this._edaraCoreApiBaseUrl = this._edaraCoreApiBaseUrl.slice(0, -1);
    }
    this._shopifyCoreApiBaseUrl = AppSettingsService.appSettings.shopifyConnectorApi.baseUrl;
    if (this._shopifyCoreApiBaseUrl.endsWith('/')) {
      this._shopifyCoreApiBaseUrl = this._shopifyCoreApiBaseUrl.slice(0, -1);
    }
    this.checkAndDisplayStoredMessage();

    // The Keycloak APP_INITIALIZER has already resolved (it depends only on KeycloakAuthService,
    // so it runs in parallel with AppSettings/Translate loads without forcing AuthService to
    // construct before those settings exist). Whenever AuthService is first injected — which is
    // strictly after all APP_INITIALIZERs complete — we sync any tokens Keycloak now holds.
    if (this.keycloakAuthService.tokens?.authenticated) {
      this.syncTokensFromKeycloak();
    }

    // A Keycloak SSO session killed elsewhere (Edara 2 logout, admin console, "sign out all
    // sessions") is pushed to this tab directly via the backend's backchannel-logout -> SignalR
    // SessionEnded event (see SignalrService), rather than being detected by polling Keycloak.
    this.signalRService.sessionEnded$.subscribe(() => {
      if (this.isAuthenticated()) {
        this.loggingService.warning('Keycloak SSO session ended (logout from another app/tab or admin console) - logging out');
        this.logout();
      }
    });
  }

  /**
   * Trigger a redirect to Keycloak for OIDC Authorization Code Flow with PKCE.
   * Stores `returnUrl` in sessionStorage so the user lands back on their intended page after
   * the callback. This is the public login entry point that replaces the legacy ROPC flow.
   */
  public login(returnUrl?: string): Promise<void> {
    if (returnUrl) {
      sessionStorage.setItem('returnUrl', returnUrl);
    }
    this.loggingService.info('Initiating Keycloak SSO redirect');
    return this.keycloakAuthService.login(`${window.location.origin}/callback`);
  }

  /**
   * Completes the post-OIDC-callback chain: runs Firebase logins, emits userLoggedIn$, and
   * navigates to returnUrl or the home/setup route. Called from CallbackComponent after
   * tokens have already been synced by initializeAuth().
   */
  public async completeLoginAfterCallback(): Promise<void> {
    if (!this.isAuthenticated()) {
      this.router.navigate(['/auth/login']);
      return;
    }

    const decodedIdToken = this.keycloakAuthService.tokens?.idTokenParsed
      ?? (this.localIdToken ? new JwtHelperService().decodeToken(this.localIdToken) : undefined);

    // The UI language the user resolved on the Keycloak login page is carried by the shared
    // `current_language` cookie (see SwitchLanguageService), which the app reads at bootstrap — /callback
    // is itself a full document load, so it is already applied here. No id_token `locale` reading and no
    // post-login reload are needed.

    await this.firebaseLogin();
    await this.shopifyFirebaseLogin();

    // US 27702: the preferred organization opens by default — once per login, here,
    // so a deliberate in-session switch to another organization is never undone.
    await this.alignActiveTenantWithPreferred();

    this.setUserProfileFromIdToken(decodedIdToken);

    await this.handlePostLoginNavigation();
  }

  /**
   * Switch the fresh session's active organization to the browser's preferred one
   * when the token points elsewhere and the user is permitted for it. Best effort:
   * offline or on a Keycloak failure the session continues on the token's
   * organization (server-mode).
   */
  private async alignActiveTenantWithPreferred(): Promise<void> {
    try {
      const organizations = await this.ensureOrganizationsLoaded();
      const preferred = this.preferredOrganizationService.preferred;
      if (!preferred) return;
      if (!this.preferredOrganizationService.isPreferredPermittedFor(organizations)) return;
      if (this.currentTenant === preferred.tenantId) return;
      if (!navigator.onLine) return;

      await this.switchTenantAsync(preferred.keycloakOrgId, { navigateHome: false });
    } catch {
      // Continue on the token's organization.
    }
  }

  /**
   * Copy current tokens from the keycloak-js instance into localStorage so the rest of the
   * app (auth.interceptor, isAuthenticated, getters) continues to read from the same keys.
   * Also extracts roles from the Keycloak Organizations claims and seeds the current tenant.
   */
  private syncTokensFromKeycloak(): void {
    const tokens = this.keycloakAuthService.tokens;
    if (!tokens || !tokens.accessToken) return;

    localStorage.setItem('isLoggedIn', 'true');
    localStorage.setItem('accessToken', tokens.accessToken);
    this._accessToken = tokens.accessToken;

    if (tokens.refreshToken) {
      localStorage.setItem('refreshToken', tokens.refreshToken);
    }
    if (tokens.idToken) {
      localStorage.setItem('idToken', tokens.idToken);
    }
    localStorage.setItem('expiresAt', JSON.stringify(tokens.expiresAtMs));

    // Extract roles from the new Keycloak Organizations claims
    this._userRoles = this.getRolesFromToken();

    const decodedIdToken: any = tokens.idTokenParsed;
    if (decodedIdToken) {
      // Populate the user profile here too so currentUserId is available as soon as the session is
      // authenticated. Otherwise eager consumers (e.g. LockService) that run on the authenticated
      // window before completeLoginAfterCallback() read an empty id and build invalid Firestore paths.
      this.storeUserProfileFromIdToken(decodedIdToken);
      this.seedTenantFromIdToken(decodedIdToken);
    }

    this._userPermissions = undefined;
    // Load the user's full org list from the OIDC userinfo endpoint (async, for the org switcher)
    this.loadOrganizationsFromUserInfo();

    // Pre-feature browsers already hold local data for the active organization: adopt it as
    // the preferred one so existing installs never see the first-login picker or lose data.
    void this.preferredOrganizationService.migrateIfNeeded(this.currentOrganization);

    // Schedule token renewal
    this.scheduleRenewal();
  }

  private get localIdToken(): string | null {
    return localStorage.getItem('idToken');
  }

  /**
   * Seed the current tenant from the active organization carried by the Keycloak token
   * (see ADR-011 Challenge 7 — preserves tenant continuity during cross-system SSO).
   *
   * In the new Keycloak Organizations model the active org — and its Edara registration (tenant)
   * id — is derived from the access token's `active_organization` claim via currentOrganization.
   * This replaces the legacy id_token `active_organization_id` + app_metadata lookup that the
   * legacy-org cleanup (PR 31052) removed.
   */
  private seedTenantFromIdToken(_decodedIdToken: any): void {
    const tenantId = this.currentOrganization?.tenantId;
    if (tenantId) {
      this.currentTenant = tenantId;
    }
  }

  /**
   * Set user profile from decoded id_token claims
   */
  private setUserProfileFromIdToken(decodedIdToken: any): void {
    const profile = this.storeUserProfileFromIdToken(decodedIdToken);
    this.userLoggedIn$.emit(profile);
  }

  /**
   * Build the user profile from id_token claims and persist it to localStorage WITHOUT emitting
   * userLoggedIn$. Called during token sync so currentUserId is populated the moment the session is
   * authenticated, and again from the post-callback chain (which additionally emits the login event).
   */
  private storeUserProfileFromIdToken(decodedIdToken: any): UserProfile {
    const profile: UserProfile = {
      id: decodedIdToken.auth0_user_id || decodedIdToken.sub,
      name: decodedIdToken.name,
      nickname: decodedIdToken.nickname,
      email: decodedIdToken.email,
      picture: decodedIdToken.picture
    };
    this._userProfile = profile;
    localStorage.setItem('userProfile', JSON.stringify(profile));
    return profile;
  }

  /**
   * Handle post-login navigation
   */
  private async handlePostLoginNavigation(): Promise<void> {
    const isSetupCompleted = await this.edaraSetupService.checkEdaraSetupCompleted();

    if (isSetupCompleted) {
      const storedReturnUrl = sessionStorage.getItem('returnUrl');
      if (storedReturnUrl) {
        sessionStorage.removeItem('returnUrl');
      }
      this.leaveCallbackFor(storedReturnUrl || '/');
    } else if (this.isInRole(RolesEnum.SystemAdministrator)) {
      this.leaveCallbackFor('/system/edara-setup');
    } else {
      this.logout('auth.systemNotSetupMessage');
    }
  }

  /**
   * Navigate away from /callback to the resolved post-login destination.
   *
   * Always REPLACES the history entry so /callback is never left navigable: a browser Back into it would
   * re-run completeLoginAfterCallback(). /callback is itself a full document load (the OIDC redirect), so
   * the language read from the shared cookie at bootstrap is already applied — no extra reload is needed.
   */
  private leaveCallbackFor(destination: string): void {
    this.router.navigateByUrl(destination, { replaceUrl: true });
  }

  /**
   * Refresh tokens via keycloak-js. Delegates to Keycloak.updateToken() which uses the
   * refresh_token grant under the hood and updates the in-memory keycloak instance.
   * We then sync the new tokens back to localStorage to preserve the existing storage contract.
   */
  public renewToken(): void {
    this.unscheduleRenewal();

    if (localStorage.getItem('isLoggedIn') !== 'true') {
      return;
    }

    // Only attempt a silent refresh when keycloak-js actually holds a session. After a full page load
    // the in-memory session is empty unless init() re-established it (from the seeded refresh token or
    // the silent check-sso). If it didn't — e.g. the seeded token was expired/rotated and 3rd-party
    // cookies are blocked — keycloak.updateToken() throws "no refresh token available", and the catch
    // below would log the user out on every refresh / new tab. Instead, keep the existing localStorage
    // session; once it expires the authGuard redirects to /auth/login for a clean first-party re-auth.
    if (!this.keycloakAuthService.tokens?.authenticated) {
      this.loggingService.info('Keycloak TokenRefresh: no active keycloak session to refresh; skipping');
      return;
    }

    this.loggingService.info('Keycloak TokenRefresh: Starting token refresh');

    this.keycloakAuthService.updateToken(300)
      .then(() => {
        this.loggingService.info('Keycloak operation succeeded: TokenRefresh');
        this.syncTokensFromKeycloak();
      })
      .catch(err => {
        this.loggingService.warning('Keycloak operation failed: TokenRefresh - logging out', { Error: err?.message || err });
        this.logout();
      });
  }

  public async renewFirebaseToken(): Promise<void> {
    this.unscheduleFirebaseRenewal();

    if (this.isAuthenticated()) {
      await this.firebaseLogin();
    }
  }

  public async renewShopifyFirebaseToken(): Promise<void> {
    this.unscheduleShopifyFirebaseRenewal();

    if (this.isAuthenticated()) {
      await this.shopifyFirebaseLogin();
    }
  }

  private _isLoggingOut = false;
  /**
   * @param messageKey Translation key of a message explaining the sign-out, shown by
   *   [[checkAndDisplayStoredMessage]] on the next load. Stored before the redirect so it survives
   *   the Keycloak round trip.
   */
  public logout(messageKey?: string): void {
    // Re-entrancy guard: verifySession(), renewToken()'s refresh-failure handler, and explicit
    // user-initiated logout can now all independently reach this in quick succession (e.g. the
    // same navigation event triggering both a refresh failure and a failed session verification).
    // A browser redirect ends this tab's JS lifetime, so this flag never needs to be reset.
    if (this._isLoggingOut) return;
    this._isLoggingOut = true;

    if (messageKey) {
      localStorage.setItem('auth_message', this.translateService.instant(messageKey));
    }

    // Embedded in the Edara Portal: the portal owns the session, so it must own signing out of it.
    // Ending the Keycloak session from inside the frame would leave the portal shell around us
    // still believing it is signed in, and the RP-initiated logout redirect would land Keycloak's
    // post-logout page inside the product frame. Drop only this frame's local state and ask the
    // portal to run the real logout at top level.
    if (this.embedService.isEmbedded) {
      this.clearLocalSession();
      this.portalBridge.send({ type: 'logout-request' });
      return;
    }

    this.clearLocalSession();

    // Redirect to Keycloak logout
    this.keycloakAuthService.logout(window.location.origin);
  }

  /**
   * Tears down everything this app holds for the signed-in user — tokens, profile, scheduled
   * renewals, Firestore sessions, local data and the SignalR group — WITHOUT touching the Keycloak
   * session. Used on its own when the identity provider logout is somebody else's job: the portal's
   * when embedded, or [[logout]]'s own Keycloak redirect when standalone.
   */
  public clearLocalSession(): void {
    // Remove tokens and expiry time
    this._accessToken = '';
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('expiresAt');
    localStorage.removeItem('userProfile');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('idToken');
    localStorage.removeItem('userOrganizations');
    this._userOrganizations = undefined;
    localStorage.removeItem('firebaseExpiresAt');
    localStorage.removeItem('shopifyFirebaseExpiresAt');
    localStorage.removeItem('pinExpiresAt');

    this.unscheduleRenewal();
    this.unscheduleFirebaseRenewal();

    this.firestoreService.waitForPendingWrites().then(() => this.firestoreService.signOut());
    this.shopifyFirestoreService.signOut();

    this.clearUserData();

    this.signalRService.leaveGroup(this.currentTenant);
  }

  public isAuthenticated(): boolean {
    // Get the expiry time from localStorage
    const expiresAtString = localStorage.getItem('expiresAt');

    // Parse the expiry time or default to 0 if not available
    const expiresAt = expiresAtString ? JSON.parse(expiresAtString) : 0;

    // Check if the expiry time is a valid timestamp and whether it's in the past
    return new Date().getTime() < expiresAt;
  }

  public isInRole(roleName: string): boolean {
    return this.userRoles.includes(roleName);
  }

  public hasPermission(permissionName: string): boolean {
    return this.userPermissions.includes(permissionName);
  }

  public hasPermissions(permissions: string[]): boolean {
    return this.userPermissions.some(permission => permissions.includes(permission));
  }

  /**
   * Switches the user's active organization in Keycloak.
   * Calls Keycloak's /users/switch-organization endpoint to obtain a new token whose
   * `active_organization` claim points at the requested Keycloak org, then refreshes
   * local state from the new token.
   */
  public switchTenant(keycloakOrgId: string): void {
    // Failures are already logged inside switchTenantAsync; fire-and-forget keeps
    // the legacy call sites unchanged.
    this.switchTenantAsync(keycloakOrgId).catch(() => { });
  }

  /**
   * Awaitable organization switch. Resolves after the new tokens are persisted, the tenant
   * is updated and tenantSwitched$ has been emitted; rejects when Keycloak refuses or the
   * network fails (switching requires a connection). Pass navigateHome: false when a flow
   * (e.g. changing the preferred organization) owns the navigation itself.
   */
  public async switchTenantAsync(keycloakOrgId: string, opts?: { navigateHome?: boolean }): Promise<void> {
    if (!keycloakOrgId) {
      throw new Error('switchTenantAsync requires a Keycloak organization id');
    }

    const oldTenantId = this.currentTenant;
    const switchUrl = `${environment.keycloakConfig.url}/realms/${environment.keycloakConfig.realm}/users/switch-organization`;

    try {
      // Native fetch bypasses AuthInterceptor (which auto-injects a TenantId header
      // that Keycloak's CORS does not allow).
      const response = await fetch(switchUrl, {
        method: 'PUT',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.accessToken}`
        },
        body: JSON.stringify({ id: keycloakOrgId })
      });

      if (!response.ok) {
        this.loggingService.warning('Keycloak switch-organization failed', { Status: response.status });
        throw new Error(`Keycloak switch-organization failed with status ${response.status}`);
      }

      const tokenResponse = await response.json();

      // Persist new tokens
      localStorage.setItem('accessToken', tokenResponse.access_token);
      this._accessToken = tokenResponse.access_token;

      if (tokenResponse.refresh_token) {
        localStorage.setItem('refreshToken', tokenResponse.refresh_token);
      }

      if (tokenResponse.id_token) {
        localStorage.setItem('idToken', tokenResponse.id_token);
      }

      if (tokenResponse.expires_in) {
        const expiresAt = tokenResponse.expires_in * 1000 + new Date().getTime();
        localStorage.setItem('expiresAt', JSON.stringify(expiresAt));
      }

      // Bring keycloak-js onto the switched organization too, so the next renewToken()
      // (it runs on every navigation) does not copy its page-load token back over this one.
      await this.refreshKeycloakAfterSwitch();

      // Invalidate cached claims so they re-read from the new token
      this._userRoles = undefined;
      this._userPermissions = undefined;

      // Update current tenant from the new active_organization claim
      const newTenantId = this.currentOrganization?.tenantId;
      if (newTenantId) {
        this.currentTenant = newTenantId;
      }

      this.tenantSwitched$.emit(this.currentTenant);
      this.signalRService.leaveGroup(oldTenantId);
      this.signalRService.joinGroup(this.currentTenant);

      // Reset renewal schedule against the new token
      this.unscheduleRenewal();
      this.scheduleRenewal();

      if (opts?.navigateHome !== false) {
        this.router.navigate(['/']);
      }
    } catch (err: any) {
      this.loggingService.warning('Failed to switch organization in Keycloak', { Error: err?.message || err });
      throw err;
    }
  }

  /**
   * After a switch, force keycloak-js to refresh (refresh_token grant). The switch moved the
   * Keycloak session to the new organization, so the refreshed tokens carry it, and keycloak-js,
   * localStorage and the API agree again. Best effort: without a keycloak-js session, or when the
   * refreshed token is for another organization, the switch response's tokens stay in place
   * and a warning is logged.
   */
  private async refreshKeycloakAfterSwitch(): Promise<void> {
    if (!this.keycloakAuthService.tokens?.authenticated) return;

    try {
      await this.keycloakAuthService.updateToken(-1);
    } catch (err: any) {
      this.loggingService.warning('Keycloak refresh after organization switch failed', { Error: err?.message || err });
      return;
    }

    const tokens = this.keycloakAuthService.tokens;
    const refreshedOrg = tokens?.accessTokenParsed?.['active_organization']?.name;
    const switchedOrg = this.getActiveOrganizationFromToken()?.name;
    if (!tokens?.accessToken || !refreshedOrg || refreshedOrg !== switchedOrg) {
      this.loggingService.warning('Keycloak refresh after organization switch returned another organization',
        { Expected: switchedOrg, Actual: refreshedOrg });
      return;
    }

    localStorage.setItem('accessToken', tokens.accessToken);
    this._accessToken = tokens.accessToken;
    if (tokens.refreshToken) {
      localStorage.setItem('refreshToken', tokens.refreshToken);
    }
    if (tokens.idToken) {
      localStorage.setItem('idToken', tokens.idToken);
    }
    localStorage.setItem('expiresAt', JSON.stringify(tokens.expiresAtMs));
  }

  public requestResetPassword(captchaCode: string, email: string): Observable<boolean> {
    return this.httpClient.post<boolean>(this._edaraCoreApiBaseUrl + '/api/users/requestResetPassword', { email: email },
      { headers: { captchaCode: captchaCode } });
  }

  public validateResetPasswordCode(code: string): Observable<boolean> {
    return this.httpClient.post<boolean>(this._edaraCoreApiBaseUrl + '/api/users/validateResetPasswordCode', { code: code });
  }
  public resetPassword(code: string, password: string): Observable<boolean> {
    const options = { headers: { 'X-Ignore-Logging': 'true' } };
    return this.httpClient.post<boolean>(this._edaraCoreApiBaseUrl + '/api/users/resetPassword', { code: code, password: password }, options);
  }

  public authenticateAsync(): Promise<string | undefined> {
    const options = { headers: { 'X-Ignore-Logging': 'true' } };
    return firstValueFrom(
      this.httpClient.get<string>(this._edaraCoreApiBaseUrl + '/api/users/authenticate', options)
    ).catch(() => { return undefined; });
  }

  public shopifyAuthenticateAsync(): Promise<string | undefined> {
    const options = { headers: { 'X-Ignore-Logging': 'true' } };
    return firstValueFrom(
      this.httpClient.get<string>(this._shopifyCoreApiBaseUrl + '/api/users/authenticate', options)
    ).catch(() => { return undefined; });
  }

  private _decodedFor?: string;
  private _decodedAccessToken?: any;
  /**
   * Decode the access token once per distinct token string. The @auth0/angular-jwt
   * decoder (b64DecodeUnicode) is expensive and the claim getters below are read
   * from templates on every change-detection pass; decoding fresh each call
   * previously cost seconds of main-thread blocking on large tokens.
   */
  private decodedAccessToken(): any {
    const token = this.accessToken;
    if (!token) {
      this._decodedFor = undefined;
      this._decodedAccessToken = undefined;
      return undefined;
    }
    if (this._decodedFor !== token) {
      this._decodedFor = token;
      this._decodedAccessToken = new JwtHelperService().decodeToken(token);
    }
    return this._decodedAccessToken;
  }

  /**
   * When the current Keycloak SSO session authenticated, in seconds since the epoch — the access token's
   * `auth_time`. It is stable across token refreshes and silent SSO re-logins (both continue the same
   * session) and changes only when the user actually signs in again, which makes it the client-side
   * handle for "this session" (see KnownDeviceRevocationService). Falls back to `iat` for a token
   * without `auth_time`.
   */
  public get sessionAuthTime(): number | undefined {
    const decodedToken = this.decodedAccessToken();
    const authTime = decodedToken?.['auth_time'] ?? decodedToken?.['iat'];
    return typeof authTime === 'number' ? authTime : undefined;
  }

  private getActiveOrganizationFromToken(): any {
    const decodedToken = this.decodedAccessToken();
    return decodedToken ? decodedToken['active_organization'] : undefined;
  }

  private getRolesFromToken(): string[] {
    const activeOrg = this.getActiveOrganizationFromToken();
    if (activeOrg && Array.isArray(activeOrg.role)) {
      return activeOrg.role;
    }
    return [];
  }

  private getPermissionsFromToken(): string[] {
    const decodedToken = this.decodedAccessToken();
    if (decodedToken && Array.isArray(decodedToken['permissions'])) {
      return decodedToken['permissions'];
    }
    return [];
  }

  private getEdaraUserIdFromToken(): number | undefined {
    const decodedToken = this.decodedAccessToken();
    const raw = decodedToken?.['org_member_attributes']?.['edara_user_id'];
    if (raw == null) return undefined;
    const parsed = Array.isArray(raw) ? Number(raw[0]) : Number(raw);
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  private getTenantIdFromToken(): string | undefined {
    const activeOrg = this.getActiveOrganizationFromToken();
    const attr = activeOrg?.attribute?.edara_registration_org_id;
    if (Array.isArray(attr) && attr.length > 0) return attr[0];
    return undefined;
  }

  /**
   * Load the user's full organization list from the Keycloak OIDC userinfo endpoint.
   * The Keycloak Organizations feature exposes member orgs as `organization` (or `organizations`)
   * in the userinfo response, shaped as a map: { [keycloakOrgId]: { name } }.
   */
  private _organizationsLoad?: Promise<void>;
  private loadOrganizationsFromUserInfo(): void {
    if (!this.accessToken) return;

    this._organizationsLoad = this.keycloakAuthService.userInfo(this.accessToken)
      .then((profile: any) => {
        if (!profile) return;
        const orgsMap = profile?.['organization'] ?? profile?.['organizations'] ?? {};
        const orgs: Organization[] = Object.entries(orgsMap).map(([keycloakOrgId, data]: [string, any]) => ({
          keycloakOrgId,
          name: data?.name ?? keycloakOrgId
        }));
        this._userOrganizations = orgs;
        localStorage.setItem('userOrganizations', JSON.stringify(orgs));

        // currentOrganization can only resolve its keycloakOrgId once the org list exists,
        // so the pre-feature migration gets a second chance here (no-op when already set).
        void this.preferredOrganizationService.migrateIfNeeded(this.currentOrganization);
      })
      .catch(err => {
        this.loggingService.warning('Failed to load organizations from Keycloak userinfo', { Error: err?.message || err });
      });
  }

  /**
   * Resolves once the organization list for the signed-in user is available. On a fresh
   * first login the userinfo request may still be in flight while guards already run;
   * they must not decide "single vs multiple organizations" on an empty list.
   */
  public async ensureOrganizationsLoaded(): Promise<Organization[]> {
    if (this.userOrganizations.length === 0 && this._organizationsLoad) {
      await this._organizationsLoad;
    }
    return this.userOrganizations;
  }

  private _refreshSubscription$?: Subscription;
  private scheduleRenewal() {
    if (!this.isAuthenticated()) return;

    const now = Date.now();

    // Use the delay in a timer to run the refresh at the proper time
    var refreshAt = now + (2 * 60 * 60 * 1000); // Refresh after 2 hours
    const source = timer(Math.max(1, refreshAt - now));

    // Once the delay time from above is reached, get a new JWT and schedule additional refreshes
    this._refreshSubscription$ = source.subscribe({
      next: () => {
        this.renewToken();
      }
    });
  }
  private unscheduleRenewal() {
    if (!this._refreshSubscription$) return;
    this._refreshSubscription$.unsubscribe();
  }

  private async firebaseLogin() {
    const firebaseAccessToken = await this.authenticateAsync();
    if (firebaseAccessToken) {
      await this.firestoreService.signIn(firebaseAccessToken);
      const decodedToken = new JwtHelperService().decodeToken(firebaseAccessToken);
      if (decodedToken) {
        // Set the time that the firebase access token will expire at
        const expiresAt = decodedToken['exp'] * 1000;
        localStorage.setItem('firebaseExpiresAt', JSON.stringify(expiresAt));

        this.scheduleFirebaseRenewal();
      }
    }
  }

  private async shopifyFirebaseLogin() {
    const firebaseAccessToken = await this.shopifyAuthenticateAsync();
    if (firebaseAccessToken) {
      await this.shopifyFirestoreService.signIn(firebaseAccessToken);
      const decodedToken = new JwtHelperService().decodeToken(firebaseAccessToken);
      if (decodedToken) {
        const expiresAt = decodedToken['exp'] * 1000;
        localStorage.setItem('shopifyFirebaseExpiresAt', JSON.stringify(expiresAt));
        this.scheduleShopifyFirebaseRenewal();
      }
    }
  }

  private _refreshFirebaseSubscription$?: Subscription;
  private scheduleFirebaseRenewal() {
    if (!this.isAuthenticated()) return;
    // Get the expiry time from localStorage
    const expiresAtString = localStorage.getItem('firebaseExpiresAt');

    // Parse the expiry time or default to 0 if not available
    const expiresAt = expiresAtString ? JSON.parse(expiresAtString) : 0;

    const now = new Date(new Date().toUTCString()).getTime();

    // Use the delay in a timer to run the refresh at the proper time
    var refreshAt = expiresAt - (1000 * 30); // Refresh 30 seconds before expiry

    const source = timer(Math.max(1, refreshAt - now));

    // Once the delay time from above is reached, get a new JWT and schedule additional refreshes
    this._refreshFirebaseSubscription$ = source.subscribe({
      next: () => {
        this.renewFirebaseToken();
      }
    });
  }
  private unscheduleFirebaseRenewal() {
    if (!this._refreshFirebaseSubscription$) return;
    this._refreshFirebaseSubscription$.unsubscribe();
  }

  private _refreshShopifyFirebaseSubscription$?: Subscription;
  private scheduleShopifyFirebaseRenewal() {
    if (!this.isAuthenticated()) return;
    // Get the expiry time from localStorage
    const expiresAtString = localStorage.getItem('shopifyFirebaseExpiresAt');
    // Parse the expiry time or default to 0 if not available
    const expiresAt = expiresAtString ? JSON.parse(expiresAtString) : 0;
    const now = new Date(new Date().toUTCString()).getTime();
    // Use the delay in a timer to run the refresh at the proper time
    var refreshAt = expiresAt - (1000 * 30); // Refresh 30 seconds before expiry
    const source = timer(Math.max(1, refreshAt - now));
    // Once the delay time from above is reached, get a new JWT and schedule additional refreshes
    this._refreshShopifyFirebaseSubscription$ = source.subscribe({
      next: () => {
        this.renewShopifyFirebaseToken();
      }
    });
  }
  private unscheduleShopifyFirebaseRenewal() {
    if (!this._refreshShopifyFirebaseSubscription$) return;
    this._refreshShopifyFirebaseSubscription$.unsubscribe();
  }

  private async clearUserData() {
    await this.clearAccounts();
  }

  private async clearAccounts() {
    this.localDbProvider.db.accounts.clear();

    const syncMetadata = await this.localDbProvider.db.syncMetadata
      .where('tenantId').equalsIgnoreCase(this.currentTenant)
      .and((itm: any) => itm.entityName === 'Account')
      .and((itm: any) => itm.statusCode === 200)
      .toArray();

    if (syncMetadata.length > 0) {
      this.localDbProvider.db.syncMetadata
        .bulkDelete(syncMetadata.map((itm: any) => itm.id))
        .catch((err: any) => this.localDbProvider.handleErrors(err));
    }
  }

  private checkAndDisplayStoredMessage(): void {
    const message = localStorage.getItem('auth_message');
    if (message) {
      setTimeout(() => {
        this.snackBar.open(message, '✖', { direction: this.translateService.instant('uiDirection') });
        localStorage.removeItem('auth_message');
      });
    }
  }
}
